/**
 * TableSeedPicker — the searchable table/view picker behind the Query
 * Editor's "Query a table" affordance. Anchored at the caret line (same pixel
 * math as the compact VQE popover), it renders the same cmdk list the VQE's
 * FROM / JOIN pickers use, plus a footer that previews the EXACT statement a
 * pick will write into the buffer — the seed is never a hidden template.
 *
 * Keys: Enter seeds, runs, and opens the compact editor; Shift+Enter seeds
 * and runs only. Escape / outside click dismiss without a pick.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { Button } from '../../../../components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '../../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../../components/ui/command';
import type { DatabaseEngineId } from '../../../../core/engines';
import { buildTableSeedSql } from './buildTableSeedSql';
import type { TableSeedGroup, TableSeedTarget } from './tableSeedGroups';

export type TableSeedPickOptions = {
  /** Shift+Enter / shift-click: seed and run, but leave the compact editor closed. */
  readonly openEditor: boolean;
};

export interface TableSeedPickerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Editor-relative pixel top of the caret line the picker anchors to. */
  readonly anchorTop: number;
  readonly engineId: DatabaseEngineId;
  readonly groups: readonly TableSeedGroup[];
  /** Objects still loading for this connection + database. */
  readonly loading: boolean;
  /** The table / view listing failed — the engine's message. */
  readonly loadError?: string;
  /** Load the table list again after `loadError`. */
  readonly onRetry: () => void;
  readonly onPick: (target: TableSeedTarget, options: TableSeedPickOptions) => void;
}

const SEARCH_PLACEHOLDER = 'Search tables and views…';

export function TableSeedPicker({
  open,
  onOpenChange,
  anchorTop,
  engineId,
  groups,
  loading,
  loadError,
  onRetry,
  onPick,
}: TableSeedPickerProps) {
  const [activeValue, setActiveValue] = useState('');
  const shiftHeldRef = useRef(false);

  const itemByValue = useMemo(() => {
    const map = new Map<string, TableSeedTarget>();
    for (const group of groups) {
      for (const item of group.items) map.set(item.value, item.target);
    }
    return map;
  }, [groups]);
  const itemByKey = useMemo(() => {
    const map = new Map<string, TableSeedTarget>();
    for (const group of groups) {
      for (const item of group.items) map.set(item.key, item.target);
    }
    return map;
  }, [groups]);

  // Focus: the search box when the list is there; otherwise the picker itself
  // (loading) or its Retry button (failed). Leaving focus in the editor meant
  // Monaco swallowed Escape, so a picker opened while loading could not be closed.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const focusBest = (content: HTMLElement | null) => {
    const target =
      content?.querySelector<HTMLElement>('input') ??
      content?.querySelector<HTMLElement>('[data-testid="table-seed-picker-retry"]') ??
      content;
    target?.focus();
  };
  const showingList = !loading && loadError === undefined;
  useEffect(() => {
    // The list replaced "Loading tables…": move focus into its search box, but
    // only if focus is still on the picker (never steal it from elsewhere).
    const content = contentRef.current;
    if (!open || !showingList || !content) return;
    if (content.contains(document.activeElement)) focusBest(content);
  }, [open, showingList]);

  const activeTarget = itemByValue.get(activeValue) ?? null;
  const previewSql = activeTarget
    ? buildTableSeedSql(engineId, activeTarget.schema, activeTarget.name)
    : null;

  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

  // cmdk's own keydown runs after ours on the same element, so the modifier
  // state is captured before its Enter handler fires the item's onSelect.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    shiftHeldRef.current = event.shiftKey;
  };

  const handleSelect = (key: string) => {
    const target = itemByKey.get(key);
    if (!target) return;
    onPick(target, { openEditor: !shiftHeldRef.current });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <div className="absolute" style={{ left: 2, top: anchorTop, width: 1, height: 1 }} aria-hidden />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-[min(26rem,90vw)] p-0 text-xs"
        data-testid="table-seed-picker"
        ref={contentRef}
        onOpenAutoFocus={(e) => {
          // Let cmdk's input take focus rather than the content wrapper.
          e.preventDefault();
          focusBest(e.currentTarget as HTMLElement | null);
        }}
      >
        {loadError !== undefined ? (
          <div className="space-y-2 p-3 text-muted-foreground" data-testid="table-seed-picker-error">
            <p className="text-foreground">Couldn’t load the table list.</p>
            <p className="break-words" data-testid="table-seed-picker-error-detail">{loadError}</p>
            <Button type="button" variant="outline" size="sm" onClick={onRetry} data-testid="table-seed-picker-retry">
              Retry
            </Button>
          </div>
        ) : loading ? (
          <p className="p-3 italic text-muted-foreground" data-testid="table-seed-picker-loading">
            Loading tables…
          </p>
        ) : totalItems === 0 ? (
          <p className="p-3 italic text-muted-foreground" data-testid="table-seed-picker-empty">
            No tables or views in this database.
          </p>
        ) : (
          <Command value={activeValue} onValueChange={setActiveValue} onKeyDown={handleKeyDown}>
            <CommandInput
              placeholder={SEARCH_PLACEHOLDER}
              data-testid="table-seed-picker-search"
              className="h-8"
            />
            <CommandList data-testid="table-seed-picker-items" className="max-h-64">
              <CommandEmpty data-testid="table-seed-picker-no-match">No matches.</CommandEmpty>
              {groups.map((group, gi) => (
                <CommandGroup key={group.heading ?? gi} heading={group.heading}>
                  {group.items.map((item) => (
                    <CommandItem
                      key={item.key}
                      value={item.value}
                      data-testid={`table-seed-picker-item-${item.key}`}
                      onSelect={() => handleSelect(item.key)}
                      onClick={(e) => { shiftHeldRef.current = e.shiftKey; }}
                      className="gap-2"
                    >
                      <span className="flex-1 truncate font-mono">{item.label}</span>
                      {item.meta ? (
                        <span className="shrink-0 rounded border border-border px-1 text-[10px] text-muted-foreground">
                          {item.meta}
                        </span>
                      ) : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
            <div
              className="border-t border-border px-2 py-1.5 text-[11px] text-muted-foreground"
              data-testid="table-seed-picker-preview"
            >
              <div className="truncate font-mono text-foreground/80" title={previewSql ?? undefined}>
                {previewSql ?? 'Pick a table to see the statement'}
              </div>
              <div className="mt-0.5 flex justify-between gap-2">
                <span>
                  <kbd className="rounded border border-border px-1">Enter</kbd> run + edit visually
                </span>
                <span>
                  <kbd className="rounded border border-border px-1">Shift+Enter</kbd> run only
                </span>
              </div>
            </div>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
